import { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { AVP_SHIFTS, AVP_POSITION_TO_SHIFT } from "../lib/avpShifts";
import { toast } from "sonner";
import { Upload, CheckCircle2, ShieldCheck, Loader2 } from "lucide-react";
import api, { formatApiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Checkbox } from "../components/ui/checkbox";
import { RadioGroup, RadioGroupItem } from "../components/ui/radio-group";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import DisclosureBanner from "../components/DisclosureBanner";

const AVAILABILITY_OPTS = [
  { v: "weekdays", l: "Weekdays" },
  { v: "weekends", l: "Weekends" },
  { v: "mornings", l: "Mornings" },
  { v: "evenings", l: "Evenings" },
];

const POSITION_LABELS = {
  full_time: "Full-Time Delivery Driver",
  experienced_fedex: "Experienced FedEx Driver",
  avp_priority_am: AVP_SHIFTS.am_9_1.label,
  avp_priority_pm: AVP_SHIFTS.pm_4_8.label,
};
const AVP_POSITIONS = new Set(["avp_priority_am", "avp_priority_pm"]);
const LEGACY_REDIRECT = { part_time_priority: "/avp", avp_priority: "/avp" };

export default function Apply() {
  const [sp] = useSearchParams();
  const nav = useNavigate();
  const [submitting, setSubmitting] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [resume, setResume] = useState(null); // {file_id, filename}
  const [form, setForm] = useState({
    full_name: "",
    email: "",
    phone: "",
    city: "",
    state: "MD",
    zip_code: "",
    position: sp.get("position") || "full_time",
    years_driving: "1-2",
    has_delivery_experience: false,
    delivery_experience_detail: "",
    has_cdl: false,
    dot_medical_certificate: null,
    dot_medical_expiration: "",
    has_clean_record: true,
    availability: [],
    earliest_start: "",
    referral_source: "",
    notes: "",
    consent_contact: false,
    consent_background: false,
  });

  useEffect(() => {
    const p = sp.get("position");
    if (p && LEGACY_REDIRECT[p]) { nav(LEGACY_REDIRECT[p], { replace: true }); return; }
    if (p && AVP_POSITIONS.has(p)) { nav(`/apply/avp?shift=${AVP_POSITION_TO_SHIFT[p]}`, { replace: true }); return; }
    if (p && POSITION_LABELS[p]) setForm((f) => ({ ...f, position: p }));
  }, [sp]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const toggleAvail = (v) =>
    setForm((f) => ({ ...f, availability: f.availability.includes(v) ? f.availability.filter((x) => x !== v) : [...f.availability, v] }));

  const handleResume = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) return toast.error("Resume must be 5MB or smaller");
    const fd = new FormData();
    fd.append("file", file);
    setUploading(true);
    try {
      const { data } = await api.post("/uploads/resume", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setResume(data);
      toast.success("Resume uploaded");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setUploading(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.consent_contact) return toast.error("Please provide contact consent to submit.");
    setSubmitting(true);
    try {
      await api.post("/applications", { ...form, resume_file_id: resume?.file_id || null });
      setSubmitted(true);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div>
        <DisclosureBanner />
        <section className="py-24">
          <div className="max-w-2xl mx-auto px-4 text-center">
            <div className="w-20 h-20 rounded-full bg-amber-500/10 border border-amber-500/40 flex items-center justify-center mx-auto">
              <CheckCircle2 className="w-10 h-10 text-amber-400" />
            </div>
            <h1 className="mt-8 text-4xl font-extrabold uppercase display text-white">Application received</h1>
            <p className="mt-4 text-slate-300 leading-relaxed">
              Thank you for applying to Integrity Delivered, Inc. Our recruiting team will review your submission and reach out via phone or email within 3 business days.
            </p>
            <div className="mt-8 flex gap-3 justify-center">
              <Button data-testid="apply-back-home-btn" onClick={() => nav("/")} className="bg-amber-500 hover:bg-amber-600 text-slate-950 font-bold">Back to Home</Button>
              <Button data-testid="apply-view-drivers-btn" variant="outline" className="border-slate-700 text-slate-200" onClick={() => nav("/driver-of-the-week")}>Driver of the Week</Button>
            </div>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div>
      <DisclosureBanner />
      <section className="py-12 md:py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-xs uppercase tracking-[0.25em] text-amber-400 font-semibold">Online Application</div>
          <h1 className="mt-3 text-3xl md:text-4xl font-extrabold uppercase display text-white">Apply to drive with us</h1>
          <p className="mt-3 text-slate-400 max-w-2xl">Complete the form below. All fields marked <span className="text-amber-400">*</span> are required.</p>

          <form onSubmit={submit} className="mt-10 space-y-10">
            {/* Position */}
            <section className="rounded-2xl border border-slate-800 bg-slate-900/60 p-6 md:p-8">
              <h2 className="text-lg font-bold uppercase display text-white">Position</h2>
              <div className="mt-4">
                <Label className="text-slate-300">Preferred position <span className="text-amber-400">*</span></Label>
                <Select value={form.position} onValueChange={(v) => (AVP_POSITIONS.has(v) ? nav(`/apply/avp?shift=${AVP_POSITION_TO_SHIFT[v]}`) : set("position", v))}>
                  <SelectTrigger data-testid="apply-position-trigger" className="mt-2 bg-slate-950 border-slate-700 text-white"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-slate-900 border-slate-700 text-white">
                    {Object.entries(POSITION_LABELS).map(([v, l]) => (
                      <SelectItem key={v} value={v} data-testid={`apply-position-opt-${v}`}>{l}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </section>

            {/* Contact */}
            <section className="rounded-2xl border border-slate-800 bg-slate-900/60 p-6 md:p-8">
              <h2 className="text-lg font-bold uppercase display text-white">Contact Information</h2>
              <div className="mt-4 grid md:grid-cols-2 gap-4">
                <div>
                  <Label>Full Name <span className="text-amber-400">*</span></Label>
                  <Input data-testid="apply-full-name-input" required value={form.full_name} onChange={(e) => set("full_name", e.target.value)} className="mt-2 bg-slate-950 border-slate-700 text-white" />
                </div>
                <div>
                  <Label>Email <span className="text-amber-400">*</span></Label>
                  <Input data-testid="apply-email-input" required type="email" value={form.email} onChange={(e) => set("email", e.target.value)} className="mt-2 bg-slate-950 border-slate-700 text-white" />
                </div>
                <div>
                  <Label>Phone <span className="text-amber-400">*</span></Label>
                  <Input data-testid="apply-phone-input" required value={form.phone} onChange={(e) => set("phone", e.target.value)} className="mt-2 bg-slate-950 border-slate-700 text-white" placeholder="(410) 555-0000" />
                </div>
                <div>
                  <Label>City</Label>
                  <Input data-testid="apply-city-input" value={form.city} onChange={(e) => set("city", e.target.value)} className="mt-2 bg-slate-950 border-slate-700 text-white" />
                </div>
                <div>
                  <Label>State</Label>
                  <Input data-testid="apply-state-input" value={form.state} onChange={(e) => set("state", e.target.value)} className="mt-2 bg-slate-950 border-slate-700 text-white" />
                </div>
                <div>
                  <Label>Zip Code</Label>
                  <Input data-testid="apply-zip-input" value={form.zip_code} onChange={(e) => set("zip_code", e.target.value)} className="mt-2 bg-slate-950 border-slate-700 text-white" />
                </div>
              </div>
            </section>

            {/* Experience */}
            <section className="rounded-2xl border border-slate-800 bg-slate-900/60 p-6 md:p-8">
              <h2 className="text-lg font-bold uppercase display text-white">Driving Experience</h2>
              <div className="mt-4 space-y-4">
                <div>
                  <Label>Years of driving experience <span className="text-amber-400">*</span></Label>
                  <RadioGroup value={form.years_driving} onValueChange={(v) => set("years_driving", v)} className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-2">
                    {["<1", "1-2", "3-5", "5+"].map((v) => (
                      <label key={v} htmlFor={`yd-${v}`} className="cursor-pointer flex items-center gap-2 rounded-md border border-slate-700 bg-slate-950 px-3 py-2 text-sm text-slate-200 hover:border-amber-500/60">
                        <RadioGroupItem value={v} id={`yd-${v}`} data-testid={`apply-years-${v}`} />
                        {v} years
                      </label>
                    ))}
                  </RadioGroup>
                </div>
                <div className="grid md:grid-cols-3 gap-3 pt-2">
                  <label className="flex items-start gap-3 rounded-md border border-slate-700 bg-slate-950 p-3">
                    <Checkbox data-testid="apply-has-delivery" checked={form.has_delivery_experience} onCheckedChange={(v) => set("has_delivery_experience", !!v)} />
                    <div><div className="text-sm text-white">Delivery experience</div><div className="text-xs text-slate-400">FedEx, UPS, Amazon, DHL, etc.</div></div>
                  </label>
                  <label className="flex items-start gap-3 rounded-md border border-slate-700 bg-slate-950 p-3">
                    <Checkbox data-testid="apply-has-cdl" checked={form.has_cdl} onCheckedChange={(v) => set("has_cdl", !!v)} />
                    <div><div className="text-sm text-white">Hold a CDL</div><div className="text-xs text-slate-400">Not required for most roles</div></div>
                  </label>
                  <label className="flex items-start gap-3 rounded-md border border-slate-700 bg-slate-950 p-3">
                    <Checkbox data-testid="apply-clean-record" checked={form.has_clean_record} onCheckedChange={(v) => set("has_clean_record", !!v)} />
                    <div><div className="text-sm text-white">Clean driving record</div><div className="text-xs text-slate-400">No major violations in 3 yrs</div></div>
                  </label>
                </div>
                <div className="mt-4 rounded-md border border-slate-700 bg-slate-950 p-3 space-y-2">
                  <div className="text-sm text-white">Do you currently have a valid DOT Medical Examiner's Certificate?</div>
                  <div className="flex flex-wrap gap-4 text-sm text-slate-200">{[["yes", "Yes"], ["no", "No"], ["not_sure", "Not Sure"]].map(([v, l]) => <label key={v} className="flex items-center gap-2"><input data-testid={`apply-dot-${v}`} type="radio" name="dot_cert" checked={form.dot_medical_certificate === v} onChange={() => set("dot_medical_certificate", v)} />{l}</label>)}</div>
                  {form.dot_medical_certificate === "yes" && <label className="block text-sm text-slate-300">What is the expiration date on your current DOT Medical Examiner's Certificate? <span className="text-amber-400">*</span>
                    <input data-testid="apply-dot-expiration" type="date" required value={form.dot_medical_expiration || ""} onChange={(e) => set("dot_medical_expiration", e.target.value)} className="mt-1 block bg-slate-900 border border-slate-700 rounded p-2 text-white" /></label>}
                  <div className="text-xs text-slate-500">HR will verify any certificate before it is accepted; this helps us choose the right screening and medical qualification package.</div>
                </div>
                {form.has_delivery_experience && (
                  <div>
                    <Label>Tell us about your delivery experience</Label>
                    <Textarea data-testid="apply-delivery-detail" value={form.delivery_experience_detail} onChange={(e) => set("delivery_experience_detail", e.target.value)} className="mt-2 bg-slate-950 border-slate-700 text-white" rows={3} placeholder="Company, years, average stops per day..." />
                  </div>
                )}
              </div>
            </section>

            {/* Availability */}
            <section className="rounded-2xl border border-slate-800 bg-slate-900/60 p-6 md:p-8">
              <h2 className="text-lg font-bold uppercase display text-white">Availability</h2>
              <div className="mt-4 grid grid-cols-2 md:grid-cols-3 gap-2">
                {AVAILABILITY_OPTS.map((a) => (
                  <label key={a.v} className={`flex items-center gap-2 cursor-pointer rounded-md border px-3 py-2 text-sm ${form.availability.includes(a.v) ? "border-amber-500 bg-amber-500/10 text-amber-200" : "border-slate-700 bg-slate-950 text-slate-200"}`}>
                    <Checkbox data-testid={`apply-availability-${a.v}`} checked={form.availability.includes(a.v)} onCheckedChange={() => toggleAvail(a.v)} />
                    {a.l}
                  </label>
                ))}
              </div>
              <div className="mt-4">
                <Label>Earliest start date</Label>
                <Input data-testid="apply-earliest-start" type="date" value={form.earliest_start} onChange={(e) => set("earliest_start", e.target.value)} className="mt-2 bg-slate-950 border-slate-700 text-white max-w-xs" />
              </div>
            </section>

            {/* Resume */}
            <section className="rounded-2xl border border-slate-800 bg-slate-900/60 p-6 md:p-8">
              <h2 className="text-lg font-bold uppercase display text-white">Resume (Optional)</h2>
              <p className="text-sm text-slate-400 mt-1">PDF, DOC, or DOCX · 5MB max</p>
              <div className="mt-4 flex items-center gap-4 flex-wrap">
                <label className="cursor-pointer inline-flex items-center gap-2 rounded-md border border-dashed border-slate-600 bg-slate-950 px-4 py-3 text-sm text-slate-200 hover:border-amber-500/60">
                  {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />} Choose file
                  <input data-testid="apply-resume-input" type="file" className="hidden" accept=".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document" onChange={handleResume} />
                </label>
                {resume && <span className="text-sm text-amber-400" data-testid="apply-resume-name">✓ {resume.filename}</span>}
              </div>
            </section>

            {/* Notes + consent */}
            <section className="rounded-2xl border border-slate-800 bg-slate-900/60 p-6 md:p-8 space-y-5">
              <div>
                <Label>Referral source</Label>
                <Input data-testid="apply-referral-input" value={form.referral_source} onChange={(e) => set("referral_source", e.target.value)} className="mt-2 bg-slate-950 border-slate-700 text-white" placeholder="Indeed, flyer QR, friend..." />
              </div>
              <div>
                <Label>Additional notes</Label>
                <Textarea data-testid="apply-notes-input" value={form.notes} onChange={(e) => set("notes", e.target.value)} className="mt-2 bg-slate-950 border-slate-700 text-white" rows={3} />
              </div>

              <div className="rounded-md border border-amber-500/30 bg-amber-500/5 p-4 flex gap-3">
                <ShieldCheck className="w-5 h-5 text-amber-400 flex-shrink-0" />
                <div className="text-xs text-slate-300 leading-relaxed">
                  <strong className="text-amber-400">Privacy notice:</strong> We collect only the information necessary to evaluate your application. We do <strong>not</strong> collect Social Security numbers, driver's license images, or other sensitive onboarding documents on this form — those are handled directly and securely after an initial interview. Your application is stored privately and reviewed only by authorized recruiting staff.
                </div>
              </div>

              <label className="flex items-start gap-3">
                <Checkbox data-testid="apply-consent-contact" checked={form.consent_contact} onCheckedChange={(v) => set("consent_contact", !!v)} />
                <span className="text-sm text-slate-300">I consent to Integrity Delivered, Inc. contacting me via phone, email, or SMS about this application. <span className="text-amber-400">*</span></span>
              </label>
              <label className="flex items-start gap-3">
                <Checkbox data-testid="apply-consent-background" checked={form.consent_background} onCheckedChange={(v) => set("consent_background", !!v)} />
                <span className="text-sm text-slate-300">I understand that employment is contingent on passing a background check, MVR review, DOT physical, and drug test, which will be initiated only after a conditional offer.</span>
              </label>
            </section>

            <div className="flex justify-end">
              <Button data-testid="apply-submit-btn" type="submit" size="lg" disabled={submitting || !form.consent_contact} className="bg-amber-500 hover:bg-amber-600 text-slate-950 font-bold uppercase tracking-wider px-10">
                {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null} Submit Application
              </Button>
            </div>
          </form>
        </div>
      </section>
    </div>
  );
}
